import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener,
         Input, OnDestroy, Output, ViewChild
       } from '@angular/core';

import { ToolboxConstants } from 'core/constants';
import { FontInjectorService } from '../collage-designer/font-injector.service';
import { TextPlaceholder
       } from '../collage-designer/stage/placeholder/text-placeholder';
import { Utils } from '../core/utils';
import { FontDescription, insertChunks, normalizedFontFamily,
         plaintextToChunks, selectCompleteText, Text, TextChunk, TextChunkKind,
         textsAreEqual,
       } from './text.model';
import { applyModel, extractTextChunks } from './text.view.div';

enum TextViewState {
  Initial,
  Reopened,
  Blurred,
  ReceivesFocus,
  Focused
}

@Component({
    selector: 'editable-text-view',
    templateUrl: './text.view.div.component.html',
    styleUrls: ['./text.view.div.component.scss'],
    standalone: false
})
export class EditableTextViewComponent implements AfterViewInit, OnDestroy
{
  private static readonly FOCUS_GRACE_TIME: number = 300; /* ms */
  @ViewChild('textArea') private textArea: ElementRef;

  private model: Text | undefined = undefined;
  private font: FontDescription | undefined = undefined;
  private textViewState: TextViewState = TextViewState.Initial;
  private rootNode: Document | ShadowRoot = undefined;

  @Output('textChange') public textChange: EventEmitter<Array<TextChunk>> =
    new EventEmitter();

  constructor(private fontInjector: FontInjectorService)
  {}

  public ngAfterViewInit(): void {
    const editableDiv = this.textArea.nativeElement;
    this.rootNode = editableDiv.getRootNode() as Document | ShadowRoot;

    document.body.appendChild(editableDiv);
    this.hideEditableDiv(editableDiv);
    setTimeout(() => {
      this.showEditableDiv(editableDiv);
      this.updatePosition();
    }, ToolboxConstants.AFTER_TEXT_TOOLBOX_ANIMATION);
  }

  public ngOnDestroy(): void {
    document.body.removeChild(this.textArea.nativeElement);
  }

  @Input('placeholder') public set placeholder(placeholder: TextPlaceholder)
  {
    if (!placeholder.isUnchanged()) {
      this.textViewState = TextViewState.Reopened;
    }
  }

  @Input() public set textModel(model: Text | undefined)
  {
    this.model = model;
    this.updateView();
  }

  @HostListener('window:resize')
  private onResize(): void {
    this.updatePosition();
  }

  private updatePosition(): void {
    if (!this.rootNode) {
       return;
    }

    const editableTextView = this.rootNode.querySelector('editable-text-view');
    const editableRect = editableTextView.getBoundingClientRect();

    const textArea = document.getElementById('editable') as HTMLElement;
    if (textArea) {
      textArea.style.top = `${editableRect.top + window.scrollY}px`;
      textArea.style.left = `${editableRect.left}px`;
      return;
    }
  }

  private showEditableDiv(editableDiv: HTMLElement) {
    if (Utils.isSafari()) {
      editableDiv.classList.add('show-div-safari');
    }
    else {
      editableDiv.classList.add('show-div');
      editableDiv.focus({ preventScroll: true });
    }
  }

  private hideEditableDiv(editableDiv: HTMLElement) {
    if (Utils.isSafari()) {
      editableDiv.classList.add('hide-div-safari');
    }
    else {
      editableDiv.classList.add('hide-div');
    }

  }

  public onInput(ev: Event): void
  {
    if (! (<any>ev).isComposing) {
      this.emitTextChange();
    }
  }

  public onBlur(): void
  {
    this.textViewState = TextViewState.Blurred;
    this.emitTextChange();
  }

  public onFocus(): void
  {
    // This is a weird and ugly workaround. When the user focuses the text field
    // on a mobile device, the keyboard will pop up and trigger a resize event.
    // This event will insert a model with a different cursor position,
    // resulting in a jumping cursor. To mitigate this we emit the cursor
    // position at the time the user focuses the text field. But because the
    // selection is set after the focus event we have to wait a little.

    if (Utils.isLargeDevice()) {
      return;
    }

    const initial = this.textViewState === TextViewState.Initial;
    this.textViewState = TextViewState.ReceivesFocus;

    setTimeout(() => {
      this.textViewState = TextViewState.Focused;
      this.textChange.emit(
        initial ? selectCompleteText(this.model).text : this.textChunks);
    }, EditableTextViewComponent.FOCUS_GRACE_TIME);
  }

  private withoutControlChunks(text: Text): Text
  {
    return {
      ...text,
      text: text.text.filter(c => c.kind !== TextChunkKind.Control)
    };
  }

  private async updateView(): Promise<void>
  {
    if (! this.model) {
      return;
    }

    if (!this.font
        || normalizedFontFamily(this.font.name)
             !== normalizedFontFamily(this.model.fontName)
        || this.font.bold !== !!this.model.fontBold
        || this.font.italic !== !!this.model.fontItalic) {
      this.font = await this.fontInjector.getFontDescription(this.model);
    }

    const displayModel = (() => {
      if (Utils.isLargeDevice()) {
        return this.model;
      }

      switch (this.textViewState) {
        case TextViewState.ReceivesFocus:
          return undefined;
        case TextViewState.Blurred:
          return this.withoutControlChunks(this.model);
        case TextViewState.Reopened:
        case TextViewState.Focused:
          return this.model;
        case TextViewState.Initial:
          return selectCompleteText(this.model);
        default:
          return undefined;
      }
    })();

    if (! displayModel) {
      return;
    }

    applyModel(this.textArea.nativeElement, displayModel, this.font);

    if (Utils.isLargeDevice() ||
        this.textViewState === TextViewState.Reopened) {
      this.textArea.nativeElement.focus();
    }
  }

  private get textChunks(): Array<TextChunk>
  {
    return extractTextChunks(this.textArea.nativeElement);
  }

  private emitTextChange(): void
  {
    const newText = this.textChunks;

    if (textsAreEqual(this.model.text, newText)) {
      return;
    }

    this.textChange.emit(newText);
  }

  public onPaste(pasteEvent: any): void
  {
    pasteEvent.stopPropagation();
    pasteEvent.preventDefault();

    const text = (pasteEvent.clipboardData || (<any>window).clipboardData)
      .getData('text');

    this.textChange.emit(
      insertChunks(this.textChunks, plaintextToChunks(text)));
  }
}
